import { useEffect, useState } from "react";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient } from "@/lib/api";

const REGIME_ICON = {
  BULL: <TrendingUp className="h-5 w-5 text-emerald-400" />,
  BEAR: <TrendingDown className="h-5 w-5 text-rose-400" />,
  SIDEWAYS: <Minus className="h-5 w-5 text-slate-400" />,
};

const REGIME_COLOR = {
  BULL: "text-emerald-400 border-emerald-400/40 bg-emerald-400/10",
  BEAR: "text-rose-400 border-rose-400/40 bg-rose-400/10",
  SIDEWAYS: "text-slate-300 border-slate-400/40 bg-slate-400/10",
};

const fmtPct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}%`);
const fmtNum = (v, digits = 4) => (v === null || v === undefined ? "—" : Number(v).toFixed(digits));

export const AdminStrategyDirectionPerformancePage = () => {
  const [regime, setRegime] = useState(null);
  const [regimeLoading, setRegimeLoading] = useState(true);
  const [regimeError, setRegimeError] = useState(null);

  const [perfData, setPerfData] = useState(null);
  const [perfLoading, setPerfLoading] = useState(true);
  const [perfError, setPerfError] = useState(null);

  const [days, setDays] = useState(14);
  const [minTrades, setMinTrades] = useState(3);

  const fetchRegime = async (force = false) => {
    setRegimeLoading(true);
    setRegimeError(null);
    try {
      const { data } = await apiClient.get(`/admin/market-regime/current${force ? "?force_refresh=true" : ""}`);
      setRegime(data);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.message || "regime_fetch_failed";
      setRegimeError(msg);
    } finally {
      setRegimeLoading(false);
    }
  };

  const fetchPerf = async () => {
    setPerfLoading(true);
    setPerfError(null);
    try {
      const { data } = await apiClient.get(`/admin/strategy-direction-performance?days=${days}&min_trades=${minTrades}`);
      setPerfData(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "perf_fetch_failed";
      setPerfError(String(msg));
    } finally {
      setPerfLoading(false);
    }
  };

  useEffect(() => {
    fetchRegime(false);
    fetchPerf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApplyFilters = () => {
    fetchPerf();
  };

  return (
    <section className="space-y-6" data-testid="strategy-direction-perf-page">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="page-title">
          Strateji Yön Analizi & Piyasa Rejimi
        </h1>
        <p className="mt-1.5 text-sm text-slate-400">
          Long/Short ayrı performans + BTC 4h EMA-200 bazlı L4 piyasa rejimi.
        </p>
      </header>

      {/* ── Market Regime Card ── */}
      <div
        className="rounded-xl border border-slate-700 bg-slate-900/60 p-5"
        data-testid="market-regime-card"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium" data-testid="regime-card-title">L4 Piyasa Rejimi</h2>
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchRegime(true)}
            disabled={regimeLoading}
            data-testid="regime-refresh-btn"
          >
            {regimeLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Live yenile</span>
          </Button>
        </div>

        {regimeError && (
          <div
            className="mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-300"
            data-testid="regime-error"
          >
            ⚠ Rejim alınamadı: {regimeError}. (US-region pod ise Binance bloklanmış olabilir; üretimde
            sorun yaşanmaz.)
          </div>
        )}

        {regimeLoading && !regime && <p className="mt-3 text-sm text-slate-400">Yükleniyor…</p>}

        {regime && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${REGIME_COLOR[regime.regime] || ""}`} data-testid="regime-badge">
              {REGIME_ICON[regime.regime] || null}
              <div>
                <div className="text-xs uppercase tracking-widest opacity-70">Rejim</div>
                <div className="text-xl font-semibold" data-testid="regime-label">{regime.regime}</div>
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">BTC Close</div>
              <div className="font-mono text-lg" data-testid="regime-close">${fmtNum(regime.close, 2)}</div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">EMA-200 (4h)</div>
              <div className="font-mono text-lg" data-testid="regime-ema200">${fmtNum(regime.ema200, 2)}</div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950/40 px-3 py-3">
              <div className="text-xs uppercase tracking-widest text-slate-500">Long / Short Bias</div>
              <div className="font-mono text-lg" data-testid="regime-bias">
                {regime.long_bias_pct}% / {regime.short_bias_pct}%
              </div>
            </div>
          </div>
        )}

        {regime && (
          <p className="mt-3 text-xs text-slate-500" data-testid="regime-meta">
            Kaynak: <span className="font-mono">{regime.source}</span> • ratio={regime.ratio} • {regime.computed_at}
          </p>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap items-end gap-3" data-testid="perf-filters">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Lookback (gün)</label>
          <Input
            type="number"
            min="1"
            max="180"
            value={days}
            onChange={(e) => setDays(Number(e.target.value) || 14)}
            className="w-28"
            data-testid="filter-days-input"
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Min İşlem</label>
          <Input
            type="number"
            min="1"
            max="100"
            value={minTrades}
            onChange={(e) => setMinTrades(Number(e.target.value) || 3)}
            className="w-24"
            data-testid="filter-mintrades-input"
          />
        </div>
        <Button onClick={handleApplyFilters} disabled={perfLoading} data-testid="apply-filters-btn">
          {perfLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          <span className={perfLoading ? "ml-2" : ""}>Uygula</span>
        </Button>
      </div>

      {/* ── Performance Tables ── */}
      {perfError && (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-300"
          data-testid="perf-error"
        >
          Performans verisi alınamadı: {perfError}
        </div>
      )}

      {perfData && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Long Top */}
          <div
            className="rounded-xl border border-emerald-500/30 bg-slate-900/60 p-4"
            data-testid="long-top-card"
          >
            <h3 className="mb-3 flex items-center gap-2 text-base font-medium text-emerald-300">
              <TrendingUp className="h-4 w-4" />
              Long-Yön Top Stratejiler
            </h3>
            <PerfTable rows={perfData.long_top_strategies} testidPrefix="long" />
            {perfData.long_top_strategies?.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="long-empty">
                Yeterli veri yok (winrate ≥ 50% & trade ≥ {minTrades} gerekli).
              </p>
            )}
          </div>

          {/* Short Top */}
          <div
            className="rounded-xl border border-rose-500/30 bg-slate-900/60 p-4"
            data-testid="short-top-card"
          >
            <h3 className="mb-3 flex items-center gap-2 text-base font-medium text-rose-300">
              <TrendingDown className="h-4 w-4" />
              Short-Yön Top Stratejiler
            </h3>
            <PerfTable rows={perfData.short_top_strategies} testidPrefix="short" />
            {perfData.short_top_strategies?.length === 0 && (
              <p className="text-sm text-slate-500" data-testid="short-empty">
                Yeterli veri yok.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Full strategies breakdown */}
      {perfData && perfData.strategies?.length > 0 && (
        <div className="rounded-xl border border-slate-700 bg-slate-900/60 p-4" data-testid="full-perf-card">
          <h3 className="mb-3 text-base font-medium">Tüm Stratejiler · Yön Bazlı Detay</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Strateji</TableHead>
                <TableHead className="text-right">Long Trades</TableHead>
                <TableHead className="text-right">Long WR</TableHead>
                <TableHead className="text-right">Long PnL</TableHead>
                <TableHead className="text-right">Short Trades</TableHead>
                <TableHead className="text-right">Short WR</TableHead>
                <TableHead className="text-right">Short PnL</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perfData.strategies.map((s) => (
                <TableRow key={s.strategy_id} data-testid={`full-perf-row-${s.strategy_id}`}>
                  <TableCell className="font-mono text-xs">{s.strategy_id}</TableCell>
                  <TableCell className="text-right font-mono">{s.long?.trades ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtPct(s.long?.winrate_pct)}</TableCell>
                  <TableCell className={`text-right font-mono ${s.long?.total_pnl > 0 ? "text-emerald-400" : s.long?.total_pnl < 0 ? "text-rose-400" : ""}`}>
                    {fmtNum(s.long?.total_pnl, 2)}
                  </TableCell>
                  <TableCell className="text-right font-mono">{s.short?.trades ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{fmtPct(s.short?.winrate_pct)}</TableCell>
                  <TableCell className={`text-right font-mono ${s.short?.total_pnl > 0 ? "text-emerald-400" : s.short?.total_pnl < 0 ? "text-rose-400" : ""}`}>
                    {fmtNum(s.short?.total_pnl, 2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {perfData && (
        <p className="text-xs text-slate-500" data-testid="perf-notes">
          {perfData.notes} • Lookback {perfData.lookback_days} gün • Üretildi {perfData.generated_at}
        </p>
      )}
    </section>
  );
};

const PerfTable = ({ rows, testidPrefix }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <Table data-testid={`${testidPrefix}-perf-table`}>
      <TableHeader>
        <TableRow>
          <TableHead>Strateji</TableHead>
          <TableHead className="text-right">Trade</TableHead>
          <TableHead className="text-right">WR%</TableHead>
          <TableHead className="text-right">Toplam PnL</TableHead>
          <TableHead className="text-right">Avg PnL</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.strategy_id} data-testid={`${testidPrefix}-row-${r.strategy_id}`}>
            <TableCell className="font-mono text-xs">{r.strategy_id}</TableCell>
            <TableCell className="text-right font-mono">{r.trades}</TableCell>
            <TableCell className="text-right font-mono">{fmtPct(r.winrate_pct)}</TableCell>
            <TableCell className={`text-right font-mono ${r.total_pnl > 0 ? "text-emerald-400" : "text-rose-400"}`}>
              {fmtNum(r.total_pnl, 2)}
            </TableCell>
            <TableCell className="text-right font-mono">{fmtNum(r.avg_pnl, 2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default AdminStrategyDirectionPerformancePage;
